import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import logo from "../../styles/logo.png";

import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";

export default function MediaUploadForm({ userId }) {
  const [mediaTitle, setMediaTitle] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [showUploaderFor, setShowUploaderFor] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  // Toast State
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch existing media
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(ENDPOINTS.getUserMedia({ userId }));
        const list = res.data || [];

        setUploadedMedia(
          list
            .filter((m) => m.activeFlag === 1)
            .map((m) => ({
              mediaId: m.mediaId,
              mediaType: m.mediaType === "PROFILE" ? "PICTURE" : m.mediaType,
              mediaTitle: m.mediaTitle || "Untitled",
              mediaName: m.mediaName,
              mediaStyle: m.mediaStyle,
              picSort: m.picSort,
            }))
        );
      } catch (err) {
        console.error("Failed to fetch media:", err);
      }
    })();
  }, [userId]);

  const validateVideoUrl = (url) => {
    const yt = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    const vim = /^(https?:\/\/)?(www\.)?vimeo\.com\/.+/;
    return yt.test(url) || vim.test(url);
  };

  // Convert URL to embed URL
  const getEmbedUrl = (url) => {
    const ytMatch = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/
    );
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

    return "";
  };

  // ------------------ UPLOAD LOGIC ------------------
  const handleUpload = async (type) => {
    if (!type || (!mediaFile && !mediaUrl)) {
      setToast({
        open: true,
        message: "Media type and file/URL required.",
        severity: "error",
      });
      return;
    }

    let finalMediaFile = null;
    let finalMediaLink = "null";

    if (type === "YTvideo" || type === "VIMvideo") {
      if (!validateVideoUrl(mediaUrl)) {
        setToast({
          open: true,
          message: "Please enter a valid YouTube or Vimeo URL.",
          severity: "error",
        });
        return;
      }

      finalMediaLink = getEmbedUrl(mediaUrl);
      finalMediaFile = "null"; // same as previous correct logic
    } else {
      finalMediaFile = mediaFile;
    }

    const formData = new FormData();
    formData.append("MediaId", 0);
    formData.append("UserId", userId);
    formData.append("MediaType", type);
    formData.append("MediaStyle", "");
    formData.append("MediaTitle", mediaTitle);
    formData.append("PicSort", 0);
    formData.append("ActiveFlag", true);
    formData.append("UserBy", userId);

    formData.append("MediaLink", finalMediaLink);
    formData.append("MediaFile", finalMediaFile);

    try {
      const res = await api.post(ENDPOINTS.addOrUpdateUserMedia(), formData);

      const newId = res.data.mediaId;

      setUploadedMedia((prev) => [
        ...prev,
        {
          mediaId: newId,
          mediaType: type,
          mediaTitle,
          mediaName: mediaFile?.name || mediaUrl,
        },
      ]);

      setMediaTitle("");
      setMediaFile(null);
      setMediaUrl("");
      setDialogOpen(false);

      setToast({
        open: true,
        message: "Media uploaded successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Upload error:", err);
      setToast({
        open: true,
        message: "Upload error. Try again.",
        severity: "error",
      });
    }
  };

  // ------------------ DELETE LOGIC ------------------
  const handleDelete = async (item) => {
    const formData = new FormData();
    formData.append("MediaId", item.mediaId);
    formData.append("UserId", userId);
    formData.append("MediaType", item.mediaType);
    formData.append("MediaStyle", item.mediaStyle || "");
    formData.append("MediaTitle", item.mediaTitle || "");
    formData.append("PicSort", item.picSort || 0);
    formData.append("ActiveFlag", false);
    formData.append("UserBy", userId);
    formData.append("MediaLink", "null");
    formData.append("MediaFile", new Blob([])); // required by backend

    try {
      await api.post(ENDPOINTS.addOrUpdateUserMedia(), formData);

      setUploadedMedia((prev) =>
        prev.filter((m) => m.mediaId !== item.mediaId)
      );

      setToast({
        open: true,
        message: "Media deleted successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Delete error:", err);
      setToast({
        open: true,
        message: "Delete failed",
        severity: "error",
      });
    }
  };

  // ------------------ RENDER SECTIONS ------------------
  const renderMediaSection = (label, type) => {
    const list = uploadedMedia.filter((m) => m.mediaType === type);

    return (
      <Box mt={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{label}</Typography>

          <IconButton
            onClick={() => {
              setShowUploaderFor(type);
              setDialogOpen(true);
            }}
          >
            <AddIcon />
          </IconButton>
        </Box>

        {list.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No media available. Add now.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {list.map((m) => (
              <Grid item xs={12} sm={6} md={4} key={m.mediaId}>
                <Box
                  position="relative"
                  border="1px solid #ccc"
                  borderRadius={2}
                  p={1}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(m)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      background: "#fff",
                      boxShadow: 1,
                      zIndex: 2,
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>

                  <Typography variant="body2">{m.mediaTitle}</Typography>

                  {type === "PICTURE" && (
                    <img
                      src={m.mediaName}
                      alt={m.mediaTitle}
                      style={{ width: "100%", borderRadius: 4 }}
                    />
                  )}

                  {type === "AUDIO" && (
                    <audio controls style={{ width: "100%" }}>
                      <source src={m.mediaName} />
                    </audio>
                  )}

                  {(type === "YTvideo" || type === "VIMvideo") && (
                    <a href={m.mediaName} target="_blank" rel="noreferrer">
                      Watch Video
                    </a>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ mt: 3 }} />
      </Box>
    );
  };

  return (
    <>
      {/* 🔥 Toast UI */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast({ ...toast, open: false })}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Box sx={{ backgroundColor: "#eee", minHeight: "100vh", py: 4 }}>
        <Box display="flex" justifyContent="center" mb={3}>
          <img src={logo} alt="DirectSubmit" />
        </Box>

        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            bgcolor: "#fff",
            p: 4,
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Typography variant="h5">Upload Your Media</Typography>

          {renderMediaSection("Photos", "PICTURE")}
          {renderMediaSection("Videos", "YTvideo")}
          {renderMediaSection("Audio", "AUDIO")}

          <Box mt={4} textAlign="center">
            <Button onClick={() => navigate("/myresume")} variant="outlined">
              Skip
            </Button>
          </Box>
        </Box>

        {/* Upload Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Add Media</DialogTitle>

          <DialogContent>
            <TextField
              fullWidth
              label="Media Title"
              value={mediaTitle}
              onChange={(e) => setMediaTitle(e.target.value)}
              sx={{ mb: 2 }}
            />

            {showUploaderFor === "YTvideo" ||
            showUploaderFor === "VIMvideo" ? (
              <TextField
                fullWidth
                label="YouTube/Vimeo URL"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
              />
            ) : (
              <Button variant="contained" component="label" fullWidth>
                Choose File
                <input
                  type="file"
                  hidden
                  accept={
                    showUploaderFor === "PICTURE"
                      ? ".jpg,.jpeg,.png,.webp,.gif"
                      : showUploaderFor === "AUDIO"
                      ? "audio/*"
                      : undefined
                  }
                  onChange={(e) => setMediaFile(e.target.files[0])}
                />
              </Button>
            )}

            {(mediaFile || mediaUrl) && (
              <Typography variant="caption" mt={1} display="block">
                Selected: {mediaFile?.name || mediaUrl}
              </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>

            <Button
              variant="contained"
              onClick={() => handleUpload(showUploaderFor)}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
